import React, { useEffect, useRef } from 'react';

interface Skill {
  name: string;
  level: number; // 0-100
  color: string;
  isMain?: boolean; // to distinguish main skills vs others
}

const Skills: React.FC = () => {
  const skillBarsRef = useRef<HTMLDivElement>(null);

  // Animation for main skill bars
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const bars = document.querySelectorAll('.skill-progress-bar');
          bars.forEach((bar) => {
            const targetWidth = bar.getAttribute('data-target-width');
            if (targetWidth) {
              bar.setAttribute('style', `width: ${targetWidth}`);
              bar.classList.add('transition-width');
            }
          });
        }
      },
      {
        root: null,
        threshold: 0.2,
      }
    );

    if (skillBarsRef.current) {
      observer.observe(skillBarsRef.current);
    }

    return () => {
      if (skillBarsRef.current) {
        observer.unobserve(skillBarsRef.current);
      }
    };
  }, []);

  const technicalSkills: Skill[] = [
    { name: 'Python', level: 90, color: 'from-blue-600 to-blue-400', isMain: true },
    { name: 'Machine Learning & AI', level: 85, color: 'from-purple-600 to-purple-400', isMain: true },
    { name: 'LangChain & RAG', level: 80, color: 'from-indigo-600 to-indigo-400', isMain: true },
    { name: 'TensorFlow & Deep Learning', level: 75, color: 'from-blue-600 to-blue-400', isMain: true },
    { name: 'Data Science Tools', level: 85, color: 'from-purple-600 to-purple-400', isMain: true },
    { name: 'C++', level: 75, color: 'from-indigo-600 to-indigo-400' },
    { name: 'C', level: 70, color: 'from-blue-600 to-blue-400' },
    { name: 'R', level: 70, color: 'from-purple-600 to-purple-400' },
    { name: 'HTML/CSS/JavaScript', level: 75, color: 'from-indigo-600 to-indigo-400' },
    { name: 'MongoDB', level: 75, color: 'from-blue-600 to-blue-400' },
    { name: 'SQL', level: 80, color: 'from-purple-600 to-purple-400' },
  ];

  const domainSkills: Skill[] = [
    { name: 'LangChain', level: 85, color: 'from-purple-600 to-purple-400' },
    { name: 'RAG Pipelines', level: 80, color: 'from-indigo-600 to-indigo-400' },
    { name: 'TensorFlow', level: 75, color: 'from-blue-600 to-blue-400' },
    { name: 'Scikit-learn', level: 80, color: 'from-purple-600 to-purple-400' },
    { name: 'NumPy', level: 85, color: 'from-indigo-600 to-indigo-400' },
    { name: 'Pandas', level: 85, color: 'from-blue-600 to-blue-400' },
    { name: 'Keras', level: 75, color: 'from-purple-600 to-purple-400' },
    { name: 'Data Preprocessing', level: 80, color: 'from-indigo-600 to-indigo-400' },
    { name: 'Supervised Learning', level: 80, color: 'from-blue-600 to-blue-400' },
    { name: 'Unsupervised Learning', level: 75, color: 'from-purple-600 to-purple-400' },
    { name: 'Deep Learning', level: 75, color: 'from-indigo-600 to-indigo-400' },
    { name: 'Generative AI', level: 85, color: 'from-blue-600 to-blue-400' },
    { name: 'Multi-Agent Systems', level: 80, color: 'from-purple-600 to-purple-400' },
    { name: 'YFinance API', level: 75, color: 'from-indigo-600 to-indigo-400' },
    { name: 'Finnhub API', level: 75, color: 'from-blue-600 to-blue-400' },
    { name: 'PDF Parsing', level: 70, color: 'from-purple-600 to-purple-400' },
    { name: 'NLP', level: 80, color: 'from-indigo-600 to-indigo-400' },
    { name: 'Supabase', level: 80, color: 'from-blue-600 to-blue-400' },
    { name: 'MongoDB', level: 75, color: 'from-purple-600 to-purple-400' },
    { name: 'Builder.io', level: 75, color: 'from-indigo-600 to-indigo-400' },
    { name: 'Lovable', level: 75, color: 'from-blue-600 to-blue-400' },
    { name: 'Cursor', level: 80, color: 'from-purple-600 to-purple-400' },
    { name: 'GitHub', level: 85, color: 'from-indigo-600 to-indigo-400' },
    { name: 'Vercel', level: 80, color: 'from-blue-600 to-blue-400' },
  ];

  // Helper function to render dots (max 5 dots)
  const renderDots = (level: number, colorClass: string) => {
    const dotsCount = Math.round((level / 100) * 5);
    return (
      <div className="flex space-x-1">
        {[...Array(5)].map((_, i) => (
          <span
            key={i}
            className={`w-3 h-3 rounded-full ${i < dotsCount ? `bg-gradient-to-r ${colorClass}` : 'bg-gray-700'}`}
          ></span>
        ))}
      </div>
    );
  };

  return (
    <section
      id="skills"
      className="py-24 bg-gradient-to-b from-black to-purple-950/5 relative overflow-hidden"
    >
      {/* Background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/3 right-1/3 w-64 h-64 bg-purple-500/5 rounded-full filter blur-3xl animate-float-slow"></div>
        <div className="absolute bottom-1/3 left-1/3 w-96 h-96 bg-blue-500/5 rounded-full filter blur-3xl animate-float-delay-slow"></div>
      </div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold font-orbitron mb-4 tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-500">
            Technical Proficiency
          </h2>
          <div className="h-1 w-20 bg-gradient-to-r from-purple-500 to-blue-500 mx-auto rounded-full mb-8"></div>
          <p className="max-w-3xl mx-auto text-lg text-gray-300">
            Technical expertise in AI/ML, data science, and software engineering developed through academic projects, internships, and hands-on experience with cutting-edge technologies.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Technical Skills */}
          <div ref={skillBarsRef} className="bg-gray-900/50 backdrop-blur-sm border border-purple-900/20 rounded-xl p-8">
            <h3 className="text-xl font-semibold font-orbitron mb-8 text-white">Technical Skills</h3>
            <div className="space-y-6">
              {technicalSkills.map((skill, index) =>
                skill.isMain ? (
                  <div key={index} className="relative">
                    <div className="flex justify-between mb-2">
                      <span className="text-gray-300">{skill.name}</span>
                      <span className="text-gray-400">{skill.level}%</span>
                    </div>
                    <div className="h-2 w-full bg-gray-800 rounded-full overflow-hidden relative">
                      <div
                        className={`skill-progress-bar h-full bg-gradient-to-r ${skill.color} rounded-full absolute left-0 top-0 transition-width`}
                        data-target-width={`${skill.level}%`}
                        style={{ width: '0%' }}
                      ></div>
                    </div>
                  </div>
                ) : (
                  <div key={index} className="flex justify-between items-center">
                    <span className="text-gray-300">{skill.name}</span>
                    {renderDots(skill.level, skill.color)}
                  </div>
                )
              )}
            </div>
          </div>

          {/* Domain Skills as tag list with colored dots */}
          <div className="bg-gray-900/50 backdrop-blur-sm border border-purple-900/20 rounded-xl p-8">
            <h3 className="text-xl font-semibold font-orbitron mb-8 text-white">Domain Expertise</h3>
            <div className="flex flex-wrap gap-4">
              {domainSkills.map((skill, index) => (
                <div
                  key={index}
                  className="flex items-center space-x-2 bg-gray-800/70 rounded-full px-4 py-1 cursor-default"
                  title={`${skill.name} - ${skill.level}% proficiency`}
                >
                  <span
                    className={`w-3 h-3 rounded-full bg-gradient-to-r ${skill.color}`}
                  ></span>
                  <span className="text-gray-300 text-sm font-medium">{skill.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Styles for smooth width transition */}
      <style>{`
        .transition-width {
          transition: width 1.5s ease-in-out;
        }
      `}</style>
    </section>
  );
};

export default Skills;